'use client';

import { useRef } from 'react';

import type { ProxyShareLinkUrlMessage } from '@/types/feedback';

export function useGetSharableLink(metaSiteId?: string) {
  const cachedUrl = useRef<string | null>(null);
  const inFlight = useRef<Promise<string | null> | null>(null);

  async function getSharableLink(): Promise<string | null> {
    if (!metaSiteId) return null;
    if (cachedUrl.current !== null) return cachedUrl.current;
    if (inFlight.current) return inFlight.current;

    const promise = new Promise<string | null>((resolve) => {
      function handleMessage(event: MessageEvent) {
        // Accept only responses re-posted by the trusted parent window
        // (PartnerPageClient). The untrusted preview iframe can post the same
        // message type directly, but never with event.source === window.
        if (event.source !== window) return;
        if (event.data?.type === 'FB_SHARE_LINK_URL') {
          cleanup();
          const shareUrl = (event.data as ProxyShareLinkUrlMessage).payload
            .shareUrl;
          cachedUrl.current = shareUrl;
          resolve(shareUrl);
        } else if (event.data?.type === 'FB_SHARE_LINK_FAILED') {
          cleanup();
          resolve(null);
        }
      }

      function cleanup() {
        window.removeEventListener('message', handleMessage);
        clearTimeout(timeout);
      }

      const timeout = setTimeout(() => {
        cleanup();
        resolve(null);
      }, 5_000);

      window.addEventListener('message', handleMessage);
      window.postMessage(
        { type: 'FB_GET_SHARE_LINK', payload: { metaSiteId } },
        window.location.origin,
      );
    });

    inFlight.current = promise;
    void promise.finally(() => {
      inFlight.current = null;
    });
    return promise;
  }

  return { getSharableLink };
}
